import React, { createContext, useContext, useEffect, useState } from 'react';
import { collection, addDoc, query, where, orderBy, onSnapshot, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { useAuth } from './AuthContext';
import { calculateTotalPrice } from '../utils/priceCalculator';
import { recordSystemRevenue, recordGirlRevenue } from '../utils/revenueManager';

const BookingContext = createContext();

export const BookingProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  // ฟังการเปลี่ยนแปลงของ bookings แบบ realtime
  useEffect(() => {
    if (!currentUser) {
      setBookings([]);
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, 'bookings'),
      where('userId', '==', currentUser.uid),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const bookingsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setBookings(bookingsData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentUser]);

  const createBooking = async (girlId, girlData, price, bookingDetails = {}) => {
    if (!currentUser) {
      throw new Error('กรุณาล็อกอินก่อน');
    }

    if (!girlId || !girlData) {
      throw new Error('ข้อมูลน้องไม่ครบถ้วน');
    }

    const { date, time, duration, message } = bookingDetails;
    
    // คำนวณข้อมูลราคาใหม่
    const pricingInfo = calculateTotalPrice(girlData, duration || 1);
    
    const bookingData = {
      userId: currentUser.uid,
      userEmail: currentUser.email,
      userName: currentUser.displayName,
      girlId: girlId,
      girlName: girlData.nickname,
      // ข้อมูลราคาเดิม (สำหรับ backward compatibility)
      price: price,
      totalPrice: price,
      // ข้อมูลราคาใหม่ (แยกราคาค่าบริการและเหรียญ)
      pricingInfo: {
        totalCoinPrice: pricingInfo.totalCoinPrice,
        totalServicePrice: pricingInfo.totalServicePrice,
        systemRevenue: pricingInfo.systemRevenue,
        girlRevenue: pricingInfo.girlRevenue,
        commission: pricingInfo.commission,
        duration: pricingInfo.duration
      },
      status: 'pending', // pending, confirmed, rejected, completed
      createdAt: new Date(),
      updatedAt: new Date(),
      // ข้อมูลการจอง
      bookingDate: date,
      bookingTime: time,
      duration: duration || 1,
      message: message || `ขอจองคิวกับ ${girlData.nickname} ในราคา ${pricingInfo.totalServicePrice} บาท (${pricingInfo.totalCoinPrice} เหรียญ)`,
      // ข้อมูลเพิ่มเติม
      endTime: time ? calculateEndTime(time, duration || 1) : null,
      // Trip Plan reference
      tripPlanId: bookingDetails.tripPlanId || null,
      tripPlanName: bookingDetails.tripPlanName || null,
      tripPlanDetails: bookingDetails.tripPlanDetails || null
    };

    try {
      console.log('🔍 Creating booking with data:', bookingData);
      const docRef = await addDoc(collection(db, 'bookings'), bookingData);
      console.log('✅ Booking created successfully:', docRef.id);
      
      // บันทึกรายได้แยกตามฝ่าย
      try {
        await recordSystemRevenue({ ...bookingData, id: docRef.id }, pricingInfo);
        await recordGirlRevenue({ ...bookingData, id: docRef.id }, pricingInfo);
      } catch (revenueError) {
        console.error('ไม่สามารถบันทึกรายได้แยกตามฝ่าย:', revenueError);
        // ไม่ throw error เพื่อไม่ให้การจองล้มเหลว
      }
      
      return docRef.id;
    } catch (error) {
      console.error('❌▸ Error in createBooking:', error);
      console.error('❌▸ Error code:', error.code);
      console.error('❌▸ Error message:', error.message);
      
      // แสดง error message ที่เป็นมิตรกับผู้ใช้
      let userMessage = 'เกิดข้อผิดพลาดในการจอง';
      if (error.code === 'permission-denied') {
        userMessage = 'ไม่มีสิทธิ์ในการจอง กรุณาติดต่อแอดมิน';
      } else if (error.code === 'unavailable') {
        userMessage = 'ระบบไม่พร้อมใช้งาน กรุณาลองใหม่ในภายหลัง';
      } else if (error.message) {
        userMessage = error.message;
      }
      
      throw new Error(userMessage);
    }
  };

  // ฟังก์ชันคำนวณเวลาสิ้นสุด
  const calculateEndTime = (startTime, duration) => {
    const [hours, minutes] = startTime.split(':').map(Number);
    const startMinutes = hours * 60 + minutes;
    const endMinutes = startMinutes + (duration * 60);
    const endHours = Math.floor(endMinutes / 60);
    const endMins = endMinutes % 60;
    return `${endHours.toString().padStart(2, '0')}:${endMins.toString().padStart(2, '0')}`;
  };

  const updateBookingStatus = async (bookingId, status) => {
    const bookingRef = doc(db, 'bookings', bookingId);
    await updateDoc(bookingRef, {
      status: status,
      updatedAt: new Date()
    });
  };

  const value = {
    bookings,
    loading,
    createBooking,
    updateBookingStatus
  };

  return (
    <BookingContext.Provider value={value}>
      {children}
    </BookingContext.Provider>
  );
};

export const useBooking = () => {
  return useContext(BookingContext);
};
